/* global Swiper */
(() => {
  const themeCreateProductSwiper = () => {
    if (!customElements.get('product-swiper')) {
      class ProductSwiper extends HTMLElement {
        connectedCallback() {
          this.bindDesktopEffect();
          this.initSwiper();
        }

        disconnectedCallback() {
          if (!this.desktopMedia || !this.onDesktopEffectChange) return;
          this.desktopMedia.removeEventListener('change', this.onDesktopEffectChange);
          this.desktopMedia = null;
        }

        usesFlatDesktop() {
          return this.dataset.desktopEffect === 'slide' && window.matchMedia('(min-width: 768px)').matches;
        }

        bindDesktopEffect() {
          if (this.dataset.desktopEffect !== 'slide' || this.desktopMedia) return;
          this.desktopMedia = window.matchMedia('(min-width: 768px)');
          this.onDesktopEffectChange = () => {
            if (!this.isConnected) return;
            this.refresh();
          };
          this.desktopMedia.addEventListener('change', this.onDesktopEffectChange);
        }

        destroySwiper() {
          if (this.swiper && !this.swiper.destroyed) {
            this.swiper.destroy(true, true);
          }
          this.swiper = null;
          this.querySelectorAll('.swiper-slide-duplicate, [data-loop-clone]').forEach((slide) => slide.remove());
        }

        fillLoopSlides(needed) {
          if (this.dataset.fillLoop !== 'true') return;
          const wrapper = this.querySelector('.swiper-wrapper');
          if (!wrapper) return;

          wrapper.querySelectorAll('[data-loop-clone]').forEach((slide) => slide.remove());
          const originals = [...wrapper.children].filter((slide) => slide.classList.contains('swiper-slide'));
          if (originals.length < 2 || originals.length >= needed) return;

          let safety = 0;
          while (wrapper.querySelectorAll(':scope > .swiper-slide').length < needed && safety < 8) {
            safety += 1;
            originals.forEach((slide) => {
              if (wrapper.querySelectorAll(':scope > .swiper-slide').length >= needed) return;
              const clone = slide.cloneNode(true);
              clone.setAttribute('data-loop-clone', 'true');
              clone.removeAttribute('id');
              clone.querySelectorAll('[id]').forEach((element) => element.removeAttribute('id'));
              wrapper.appendChild(clone);
            });
          }
        }

        initSwiper() {
          if (this.swiper && !this.swiper.destroyed) return;
          if (typeof Swiper === 'undefined') return;

          const fullWidth = this.dataset.fullWidth === 'true';
          const flatDesktop = this.usesFlatDesktop();
          const effect = flatDesktop ? 'slide' : this.dataset.effect;
          const coverflow = effect === 'coverflow';
          this.classList.toggle('product-swiper--flat', flatDesktop);
          const slidesMobile = parseInt(this.dataset.slidesMobile, 10) || 1;
          const slidesTablet = parseInt(this.dataset.slidesTablet, 10) || 3;
          const slidesDesktop = parseInt(this.dataset.slidesDesktop, 10) || 4;
          const slidesWide = parseInt(this.dataset.slidesWide, 10) || 5;
          const widest = Math.max(slidesMobile, slidesTablet, slidesDesktop, slidesWide);
          const perView = widest % 2 === 0 ? widest + 1 : widest;
          let loopAdditionalSlides = 5;

          if (this.dataset.fillLoop === 'true') {
            this.fillLoopSlides(perView + 1);
            const count = this.querySelectorAll('.swiper-wrapper > .swiper-slide').length;
            loopAdditionalSlides = Math.max(0, Math.min(5, count - perView - 1));
          }

          this.swiper = new Swiper(`#${CSS.escape(this.id)} .swiper`, {
            grabCursor: true,
            centeredSlides: !flatDesktop,
            slidesPerView: slidesMobile,
            spaceBetween: coverflow ? 50 : 24,
            breakpoints: {
              768: {
                slidesPerView: slidesTablet,
                spaceBetween: coverflow ? 80 : 40
              },
              1120: {
                slidesPerView: slidesDesktop
              },
              1400: {
                slidesPerView: slidesWide
              }
            },
            loop: true,
            loopAdditionalSlides,
            effect,
            coverflowEffect: {
              rotate: 0,
              stretch: 0,
              depth: 200,
              scale: 1,
              modifier: 1,
              slideShadows: false
            },
            navigation: {
              nextEl: `#${CSS.escape(this.id)} .slider-nav__btn[name=next]`,
              prevEl: `#${CSS.escape(this.id)} .slider-nav__btn[name=prev]`
            },
            a11y: {
              slideRole: null
            },
            on: {
              init: fullWidth ? null : this.updateEdgeSlideVisibility,
              slideChange: fullWidth ? null : this.updateEdgeSlideVisibility
            }
          });

          this.swiper.on('init', this.updateSlideTabIndex.bind(this));
          this.swiper.on('slideChange', this.updateSlideTabIndex.bind(this));
        }

        refresh() {
          this.destroySwiper();
          this.initSwiper();
        }

        updateSlideTabIndex() {
          this.swiper.slides.forEach((slide, index) => {
            const focusableElements = slide.querySelectorAll(
              'product-card, a, button, input, textarea, select, [tabindex]'
            );
            if (index === this.swiper.activeIndex) {
              slide.removeAttribute('tabindex');
              focusableElements.forEach((el) => el.removeAttribute('tabindex'));
            } else {
              slide.setAttribute('tabindex', '-1');
              focusableElements.forEach((el) => el.setAttribute('tabindex', '-1'));
            }
          });
        }

        updateEdgeSlideVisibility() {
          this.slides.forEach((slide) => slide.classList.remove('is-visible'));

          const { activeIndex } = this;
          const { slidesPerView } = this.params;
          let range;
          if (slidesPerView <= 1) {
            range = 0;
          } else if (slidesPerView <= 3) {
            range = 1;
          } else if (slidesPerView <= 4) {
            range = 2;
          } else {
            range = 3;
          }

          for (let i = -range; i <= range; i += 1) {
            const index = (activeIndex + i + this.slides.length) % this.slides.length;
            this.slides[index].classList.add('is-visible');
          }
        }
      }

      customElements.define('product-swiper', ProductSwiper);
    }
  };

  if (theme.Swiper) {
    themeCreateProductSwiper();
  } else {
    document.addEventListener('swiper-loaded', themeCreateProductSwiper);
  }
})();
